import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../lib/pocketbase';
import styles from './HeroSlides.module.css';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faGripVertical, faImage } from '@fortawesome/free-solid-svg-icons';
import { Image as ImageIcon, X, Save } from 'lucide-react';
import Button from '../../components/common/Button/Button';

function HeroSlides() {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingSlide, setEditingSlide] = useState(null);
    const [draggedIdx, setDraggedIdx] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        text: '',
        button1_text: 'Voir nos réalisations',
        button1_link: '/realisations',
        button2_text: 'Demander un devis gratuit',
        button2_link: '/contact',
        active: true,
        order: 0,
        effect: 'zoomInEffect'
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchSlides();
    }, []);

    const fetchSlides = async () => {
        try {
            const records = await pb.collection('hero_slides').getFullList({
                sort: 'order',
            });
            setSlides(records);
        } catch (error) {
            console.error('Error fetching slides:', error);
            // Don't show error if collection doesn't exist yet, we will prompt the user
            if (error.status !== 404) {
                toast.error('Erreur lors du chargement des diapositives.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (slide = null) => {
        if (slide) {
            setEditingSlide(slide);
            setFormData({
                title: slide.title || '',
                text: slide.text || '',
                button1_text: slide.button1_text || '',
                button1_link: slide.button1_link || '',
                button2_text: slide.button2_text || '',
                button2_link: slide.button2_link || '',
                active: slide.active,
                order: slide.order || 0,
                effect: slide.effect || 'zoomInEffect'
            });
            setImagePreview(slide.image ? pb.files.getURL(slide, slide.image) : null);
        } else {
            setEditingSlide(null);
            setFormData({
                title: '',
                text: '',
                button1_text: 'Voir nos réalisations',
                button1_link: '/realisations',
                button2_text: 'Demander un devis gratuit',
                button2_link: '/contact',
                active: true,
                order: slides.length,
                effect: 'zoomInEffect'
            });
            setImagePreview(null);
        }
        setImageFile(null);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingSlide(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const form = new FormData();
            Object.keys(formData).forEach(key => form.append(key, formData[key]));

            if (imageFile) {
                form.append('image', imageFile);
            }

            if (editingSlide) {
                await pb.collection('hero_slides').update(editingSlide.id, form);
                toast.success('Diapositive mise à jour !');
            } else {
                await pb.collection('hero_slides').create(form);
                toast.success('Diapositive créée !');
            }

            handleCloseForm();
            fetchSlides();
        } catch (error) {
            console.error('Error saving slide:', error);
            toast.error('Erreur lors de la sauvegarde.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Voulez-vous vraiment supprimer cette diapositive ?')) {
            try {
                await pb.collection('hero_slides').delete(id);
                toast.success('Diapositive supprimée.');
                fetchSlides();
            } catch (error) {
                console.error('Error deleting slide:', error);
                toast.error('Erreur lors de la suppression.');
            }
        }
    };

    const toggleActive = async (slide) => {
        try {
            await pb.collection('hero_slides').update(slide.id, { active: !slide.active });
            fetchSlides();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour.');
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedIdx(index);
        e.dataTransfer.effectAllowed = 'move';
        // Required for Firefox
        if (e.dataTransfer.setData) {
            e.dataTransfer.setData('text/plain', index);
        }
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIdx === null || draggedIdx === index) return;

        const newSlides = [...slides];
        const draggedSlide = newSlides[draggedIdx];

        newSlides.splice(draggedIdx, 1);
        newSlides.splice(index, 0, draggedSlide);

        setDraggedIdx(index);
        setSlides(newSlides);
    };

    const handleDragEnd = async () => {
        setDraggedIdx(null);
        try {
            await Promise.all(
                slides.map((slide, index) =>
                    pb.collection('hero_slides').update(slide.id, { order: index })
                )
            );
            toast.success('Ordre enregistré avec succès');
        } catch (error) {
            console.error('Error updating order:', error);
            toast.error('Erreur lors de la sauvegarde de l\'ordre');
            fetchSlides(); // Revert in case of failure
        }
    };

    if (loading) return <div>Chargement...</div>;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h2>Accueil - Slider (Hero)</h2>
                    <p>Gérez les images et les textes du carrousel de la page d'accueil.</p>
                </div>
                <button className={styles.addBtn} onClick={() => handleOpenForm()}>
                    <FontAwesomeIcon icon={faPlus} /> Nouvelle Diapositive
                </button>
            </div>

            <div className={styles.slidesList}>
                {slides.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>Aucune diapositive pour le moment.</p>
                    </div>
                ) : (
                    slides.map((slide, index) => (
                        <div
                            key={slide.id}
                            className={`${styles.slideCard} ${!slide.active ? styles.inactive : ''} ${draggedIdx === index ? styles.dragging : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                        >
                            <div className={styles.dragHandle} title="Déplacer">
                                <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                            <div className={styles.slideImage}>
                                {slide.image ? (
                                    <img src={pb.files.getURL(slide, slide.image, { thumb: '100x100' })} alt={slide.title} />
                                ) : (
                                    <div className={styles.placeholderImg}><FontAwesomeIcon icon={faImage} /></div>
                                )}
                            </div>
                            <div className={styles.slideInfo}>
                                <h3 className={styles.slideTitle}>{slide.title || 'Sans titre'}</h3>
                                <p className={styles.slideText}>{slide.text}</p>
                            </div>
                            <div className={styles.slideActions}>
                                <button
                                    onClick={() => toggleActive(slide)}
                                    className={`${styles.toggleBtn} ${slide.active ? styles.activeBtn : ''}`}
                                >
                                    {slide.active ? 'Visible' : 'Caché'}
                                </button>
                                <button onClick={() => handleOpenForm(slide)} className={styles.editBtn}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button onClick={() => handleDelete(slide.id)} className={styles.deleteBtn}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {isFormOpen && createPortal(
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
                    <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 700, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                            <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ec4899' }}>
                                        <ImageIcon size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{editingSlide ? 'Modifier' : 'Ajouter'} une Diapositive</h3>
                                        <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Bannière Accueil Site Web</p>
                                    </div>
                                </div>
                                <button type="button" onClick={handleCloseForm} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Image de fond *</label>
                                    <div style={{ border: '2px dashed var(--color-light-gray)', borderRadius: 12, padding: 15, textAlign: 'center', background: 'var(--color-light-background)' }}>
                                        {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, marginBottom: 10, objectFit: 'cover' }} />}
                                        <input type="file" accept="image/*" onChange={handleFileChange} required={!editingSlide && !imagePreview} style={{ width: '100%', fontSize: 14 }} />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Titre principal *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required
                                        style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Sous-titre / Texte descriptif</label>
                                    <textarea
                                        value={formData.text}
                                        onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                        rows={3}
                                        style={{ width: '100%', padding: '15px', borderRadius: 14, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 500, fontSize: 14, background: 'var(--color-surface)', color: 'var(--color-text)' }}
                                    ></textarea>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Texte Bouton Primaire</label>
                                        <input type="text" value={formData.button1_text} onChange={(e) => setFormData({ ...formData, button1_text: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Lien Bouton Primaire</label>
                                        <input type="text" value={formData.button1_link} onChange={(e) => setFormData({ ...formData, button1_link: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Texte Bouton Secondaire</label>
                                        <input type="text" value={formData.button2_text} onChange={(e) => setFormData({ ...formData, button2_text: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Lien Bouton Secondaire</label>
                                        <input type="text" value={formData.button2_link} onChange={(e) => setFormData({ ...formData, button2_link: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Effet d'animation</label>
                                        <select value={formData.effect} onChange={(e) => setFormData({ ...formData, effect: e.target.value })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }}>
                                            <option value="zoomInEffect">Zoom Avant (Défaut)</option>
                                            <option value="zoomOutEffect">Zoom Arrière</option>
                                            <option value="panRightEffect">Glissement Droite</option>
                                            <option value="panLeftEffect">Glissement Gauche</option>
                                            <option value="panDownEffect">Glissement Bas</option>
                                            <option value="panUpEffect">Glissement Haut</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Ordre d'affichage</label>
                                        <input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '12px', background: 'var(--color-light-background)', borderRadius: 12, border: '1.5px solid var(--color-light-gray)' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                            style={{ width: 18, height: 18 }}
                                        />
                                        <span style={{ fontWeight: 700, color: 'var(--color-text)' }}>Activer cette diapositive</span>
                                    </label>
                                </div>
                            </div>

                            <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                <div />
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button type="button" onClick={handleCloseForm} disabled={isSaving} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                                        Annuler
                                    </button>
                                    <button type="submit" disabled={isSaving} style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                                        <Save size={18} /> {isSaving ? 'Sauvegarde...' : 'Sauvegarder la diapositive'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default HeroSlides;
